import type { createApiClient } from '@behuman.corp/contracts'
import type { Appointment, Encounter, Patient, Practitioner } from '@medplum/fhirtypes'

type MiddlewareClient = ReturnType<typeof createApiClient>

export type ScheduleEncounter = {
  id: string
  periodStart: string
  periodEnd: string | null
  status:
    | 'planned'
    | 'arrived'
    | 'triaged'
    | 'in-progress'
    | 'onleave'
    | 'finished'
    | 'cancelled'
    | 'entered-in-error'
    | 'unknown'
  appointmentId: string | null
  appointmentStatus:
    | 'proposed'
    | 'pending'
    | 'booked'
    | 'arrived'
    | 'fulfilled'
    | 'cancelled'
    | 'noshow'
    | 'entered-in-error'
    | 'checked-in'
    | 'waitlist'
    | null
  practitionerIds: string[]
  patientId: string | null
  patientDisplayName: string | null
}

export type ListScheduleEncountersResponse = {
  encounters: ScheduleEncounter[]
}

export type ListScheduleEncountersParams = {
  dateFrom: string
  dateTo: string
  practitionerIds?: string[]
  patientId?: string
  encounterClass?: string[]
  count?: number
}

export async function fetchScheduleEncounters(
  middleware: MiddlewareClient,
  params: ListScheduleEncountersParams,
): Promise<ListScheduleEncountersResponse> {
  // Strip undefined keys so the middleware client doesn't serialize them as empty params.
  const query = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
  )

  return (await middleware.get('/schedule/encounters', query)) as ListScheduleEncountersResponse
}

export type ProgressNoteStatusValue = 'submitted' | 'not-submitted'

export type CareCoordinationEncounterItem = {
  resource: Encounter
  patient: Patient | null
  appointment: Appointment | null
  practitioners: Practitioner[]
  documentReferenceId?: string
}

export type ListCareCoordinationEncountersResponse = {
  encounters: CareCoordinationEncounterItem[]
  offset: number
  hasMore: boolean
  total?: number
}

export type ListCareCoordinationEncountersParams = {
  practitionerIds: string[]
  progressNoteStatus: ProgressNoteStatusValue[]
  dateFrom?: string
  dateTo?: string
  patientId?: string
  excludeWithStatus?: string[]
  excludeClosedBy?: string
  count?: number
  offset?: number
}

export async function fetchCareCoordinationEncounters(
  middleware: MiddlewareClient,
  params: ListCareCoordinationEncountersParams,
): Promise<ListCareCoordinationEncountersResponse> {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
  )

  return (await middleware.get(
    '/care-coordination/encounters',
    query,
  )) as ListCareCoordinationEncountersResponse
}

export async function closeEncounter(
  middleware: MiddlewareClient,
  encounterId: string,
): Promise<void> {
  await middleware.post(`/care-coordination/encounters/${encounterId}/close`, {})
}

export async function uncloseEncounter(
  middleware: MiddlewareClient,
  encounterId: string,
): Promise<void> {
  await middleware.post(`/care-coordination/encounters/${encounterId}/unclose`, {})
}

export const ENCOUNTER_CLOSED_BY_TAG_SYSTEM =
  'https://behuman.health/fhir/CodeSystem/encounter-closed-by'

export function isEncounterClosedBy(
  item: CareCoordinationEncounterItem,
  practitionerId: string,
): boolean {
  return (
    item.resource.meta?.tag?.some(
      (tag) => tag.system === ENCOUNTER_CLOSED_BY_TAG_SYSTEM && tag.code === practitionerId,
    ) ?? false
  )
}

export type ResultCategoryValue = 'lab' | 'imaging'
export type ResultSeverityValue = 'critical' | 'abnormal' | 'review' | 'available'

export type ResultItem = {
  id: string
  patientId: string | null
  patientDisplayName: string | null
  effectiveAt: string | null
  category: ResultCategoryValue
  codeText: string | null
  conclusion: string | null
  status:
    | 'registered'
    | 'partial'
    | 'preliminary'
    | 'final'
    | 'amended'
    | 'corrected'
    | 'appended'
    | 'cancelled'
    | 'entered-in-error'
    | 'unknown'
  severity: ResultSeverityValue
  hasAttachment: boolean
  isNew: boolean
  isAbnormal: boolean
  isClosedByCurrentUser: boolean
}

export type ListResultsResponse = {
  results: ResultItem[]
  offset: number
  hasMore: boolean
  total?: number
}

export type ListResultsParams = {
  category?: ResultCategoryValue
  patientIds?: string[]
  practitionerIds?: string[]
  dateFrom?: string
  dateTo?: string
  count?: number
  offset?: number
  showClosed?: boolean
}

export async function closeResult(middleware: MiddlewareClient, resultId: string): Promise<void> {
  await middleware.post(`/results/diagnostic-reports/${resultId}/close`, {})
}

export async function uncloseResult(middleware: MiddlewareClient, resultId: string): Promise<void> {
  await middleware.post(`/results/diagnostic-reports/${resultId}/unclose`, {})
}

export async function fetchResults(
  middleware: MiddlewareClient,
  params: ListResultsParams,
): Promise<ListResultsResponse> {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
  )

  return (await middleware.get('/results/diagnostic-reports', query)) as ListResultsResponse
}

export type { MiddlewareThreadSummary } from '@/pages/Messages/service'

export type FetchThreadsParams = {
  onlyUrgent?: boolean
  onlyFollowUps?: boolean
  patientId?: string
}

export async function fetchThreads(
  middleware: MiddlewareClient,
  params: FetchThreadsParams = {},
): Promise<MiddlewareThreadSummary[]> {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null),
  )
  return (await middleware.get('/threads', query)) as MiddlewareThreadSummary[]
}
